import { ADDRESS_LM20_RT } from '../../protocols/address'
import { sendRead, sendWrite } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'

import { mouseKeyFeature } from '../../../../features/mouse-keys/contract'
import {
  keyAttr,
  LayerKeysMap,
  keyMapItem,
  Macro,
  mouseKey,
  combinKey,
  DPIType,
  SnipingFireType,
  FireType,
  DIYType,
  WheelType,
  WheelValueType,
  PressureMacro,
  WheelDirection,
} from '../../../../types/mouseKeys'
import { generateMacroPackageAddressLevel } from '../../protocols/addressGenerators'
import { MemoryMacroProfile, MemoryMacroStore } from '../../../shared/feature-impls/memory-macro'

const maxPackageLen = 24 // 单包最大有效数据长度

/**
 * 滚轮按键 index → 默认方向（wheel.direction 语义）。
 * 0x00 = 上滚/右滚（keycode 0x01 系），0x01 = 下滚/左滚（keycode 0xff 系）。
 * 固件的滚轮方向字节是相对该键默认方向的反向标志（0x00 顺向 / 0x01 反向），
 * 实测：index9 + 0x0c + 0x00 → 右滚，index10 + 0x0c + 0x00 → 左滚，0x0b + 0x01 → 垂直反向。
 */
const WHEEL_DEFAULT_DIRECTION: Record<number, WheelDirection> = { 9: 0, 10: 1, 11: 0, 12: 1 }
const LM20_RT_MEMORY_MACRO_PROFILE: MemoryMacroProfile = {
  packageAddresses: ADDRESS_LM20_RT.MACRO_PACKAGE_ADDRESS,
  nameAddresses: ADDRESS_LM20_RT.MACRO_NAME_ADDRESS,
  maxPackageLen,
  listBatchSize: 3,
  maxMacroPayloadLen: 4 * 1024,
  activationDelayMs: 200,
  allowEmptyMacroPayload: true,
  generatePackageAddresses: generateMacroPackageAddressLevel,
  // 鼠标宏列表旧逻辑：首字节为换行或 0 时视为无效宏。
  isEmptyListName: (bytes) => [0x0a, 0x00].includes(bytes[0]),
  isEmptyDetailName: (bytes) => bytes.every((value) => value === 255),
}

const LM20_RT_PRESSURE_MEMORY_MACRO_PROFILE: MemoryMacroProfile = {
  packageAddresses: ADDRESS_LM20_RT.MACRO_PRESSURE_ADDRESS,
  maxPackageLen,
  listBatchSize: 3,
  activationDelayMs: 200,
  allowEmptyMacroPayload: true,
  generatePackageAddresses: generateMacroPackageAddressLevel,
  isEmptyListName: (bytes) => [0x0a, 0x00].includes(bytes[0]),
}

export class LM20RTKeys extends mouseKeyFeature {
  private readonly macroStore = new MemoryMacroStore<Macro>({
    device: this.device,
    profile: LM20_RT_MEMORY_MACRO_PROFILE,
    read: sendRead,
    write: sendWrite,
  })

  private readonly pressureMacroStore = new MemoryMacroStore<PressureMacro>({
    device: this.device,
    profile: LM20_RT_PRESSURE_MEMORY_MACRO_PROFILE,
    read: sendRead,
    write: sendWrite,
  })

  /** 获取鼠标按键表 */
  async getMouseKeyMap(): Promise<LayerKeysMap> {
    console.log('[LM20RTKeys] get mouse key all information')
    const addressMap = ADDRESS_LM20_RT.MOUSE_KEY_LIST[this.device.currentConfig.index]
    const readPromises = addressMap.map((address, i) => {
      return sendRead(this.device, address, 4, (res) => {
        const keyAttr = this.disposeRead(res, i)
        return {
          index: i,
          keyAttr,
        }
      })
    })
    return Promise.all(readPromises)
  }

  /**
   * TODO 说是其他主控也差不多一样的数据结构，但没做到以前先不抽出去，后续做到了再看是否真一致
   * 因为说能做到一样的数据原因是和固件battle后的结果，是否真遵守这里先存疑
   * 下面的发送前处理disposeSend也是一样不要急着先抽
   */
  /** 鼠标按键数据处理器 */
  private disposeRead(result: DataView, index: number): keyAttr {
    const data = {} as keyAttr
    // 功能类型
    data.type = result.getUint8(4)
    switch (data.type) {
      case 0x00:
        // 键盘单键
        data.code = [result.getUint8(6)]
        break

      case 0x01:
        // 文档未定义
        break

      case 0x02:
        // 键盘组合键
        {
          // 注意这里显示顺序不是字节顺序，而是 字节3组合键 + 字节4 + 字节2
          const key1 = result.getUint8(7)
          const key2 = result.getUint8(5)
          data.code = [key1, key2]
          const combinByte = result.getUint8(6)
          data.combinKey = [
            (combinByte >> 0) & 1, // bit0 L-Ctrl
            (combinByte >> 1) & 1, // bit1 L-Shift
            (combinByte >> 2) & 1, // bit2 L-Alt
            (combinByte >> 3) & 1, // bit3 L-Win
            (combinByte >> 4) & 1, // bit4 R-Ctrl
            (combinByte >> 5) & 1, // bit5 R-Shift
            (combinByte >> 6) & 1, // bit6 R-Alt
            (combinByte >> 7) & 1, // bit7 R-Win
          ] as combinKey
        }
        break

      case 0x03:
        // 鼠标
        const mouseKey = {
          mouseKey: [0, 0, 0, 0, 0] as mouseKey,
          isDouble: false,
        }
        const mouseByte = result.getUint8(6)
        mouseKey.mouseKey = [
          (mouseByte >> 0) & 1, // bit0 左键
          (mouseByte >> 1) & 1, // bit1 右键
          (mouseByte >> 2) & 1, // bit2 中键
          (mouseByte >> 3) & 1, // bit4 按键4
          (mouseByte >> 4) & 1, // bit5 按键5
        ]
        mouseKey.isDouble = !!(result.getUint8(7) & 0x01) // bit0 是否双击
        data.mouseKey = mouseKey
        break

      case 0x04:
        // 多媒体键（大端序：Byte2=高位, Byte3=低位）
        data.code = [result.getUint16(6, false)]
        break

      case 0x05:
        // 宏定义
        const macro = {
          index: result.getUint8(6),
        }
        data.macro = macro
        break

      case 0x06:
        // 文档未定义
        break

      case 0x07:
        // 禁用按键
        break

      case 0x08:
        // DPI 按键
        const dpi = {
          type: result.getUint8(6) as DPIType,
        }
        data.dpi = dpi
        break

      case 0x09:
        // 功能按键
        const snipingFire = {
          type: result.getUint8(6) as SnipingFireType,
          fireType: result.getUint8(7) as FireType,
        }
        data.snipingFire = snipingFire
        break

      case 0x0a:
        // 自定义切换键
        const diy = {
          type: result.getUint8(6) as DIYType,
        }
        data.diy = diy
        break

      case 0x0b:
      // 前滚轮
      case 0x0c:
      // 侧滚轮
      case 0xff: {
        // 默认按键
        const valueType = result.getUint8(6) as WheelValueType
        // 从反向标志还原实际方向：0x01 为反向，0x00/0xff 保持该键默认方向
        const defaultDirection = WHEEL_DEFAULT_DIRECTION[index] ?? 0
        const direction = (
          valueType === 0x01 ? defaultDirection ^ 1 : defaultDirection
        ) as WheelDirection
        data.wheel = {
          valueType,
          type: result.getUint8(7) as WheelType,
          direction,
        }
        break
      }

      case 0x0d:
        // 任意键盘组合
        {
          const key1 = result.getUint8(5)
          const key2 = result.getUint8(6)
          const key3 = result.getUint8(7)
          data.code = [key1, key2, key3]
        }
        break

      default:
        break
    }
    return data
  }

  /** 设置鼠标按键表 */
  async setMouseKeyMap(keys: LayerKeysMap): Promise<void> {
    console.log('[LM20RTKeys] set mouse key all information')
    const addressMap = ADDRESS_LM20_RT.MOUSE_KEY_LIST[this.device.currentConfig.index]

    const writes = keys.map((item) => {
      const addr = addressMap[item.index]
      if (!addr) {
        console.warn(`Mouse key index ${item.index} out of range`)
        return Promise.resolve()
      }
      const data = this.disposeSend(item.keyAttr, item.index)
      return sendWrite(this.device, addr, Array.from(data))
    })

    await Promise.all(writes)
  }

  /** 设置指定鼠标按键 */
  async setMouseKey(key: keyMapItem, index: number): Promise<void> {
    console.log('[LM20RTKeys] set mouse key', key, index)
    const address = getAddress(ADDRESS_LM20_RT.MOUSE_KEY_LIST, index, key.index)
    const data = this.disposeSend(key.keyAttr, key.index)
    return sendWrite(this.device, address, Array.from(data))
  }
  // 发送前处理
  private disposeSend(data: keyAttr, index: number): Uint8Array {
    const buffer = new ArrayBuffer(4)
    const dv = new DataView(buffer)
    // 如果未给类型，视为默认按键
    if (data.type === undefined) {
      dv.setUint32(0, 0xffffffff, true)
    } else {
      const type = data.type
      dv.setUint8(0, type)
    }
    switch (data.type) {
      case 0x00:
        // 键盘单键
        if (data.code && data.code.length) {
          dv.setUint8(2, data.code[0])
        }
        break

      case 0x01:
        // 文档未定义
        break

      case 0x02:
        // 键盘组合键
        if (data.code && data.code.length) {
          dv.setUint8(3, data.code[0])
          dv.setUint8(1, data.code[1] ?? 0)
          let combinByte = 0
          if (data.combinKey) {
            for (let i = 0; i < data.combinKey.length; i++) {
              combinByte |= data.combinKey[i] << i
            }
          }
          dv.setUint8(2, combinByte)
        }
        break

      case 0x03:
        // 鼠标
        if (data.mouseKey) {
          let mouseByte = 0
          for (let i = 0; i < data.mouseKey.mouseKey.length; i++) {
            mouseByte |= data.mouseKey.mouseKey[i] << i
          }
          dv.setUint8(2, mouseByte)
          dv.setUint8(1, data.mouseKey.isDouble ? 0x01 : 0x00)
        }
        break

      case 0x04:
        // 多媒体键（大端序：Byte2=高位, Byte3=低位）
        if (data.code && data.code.length) {
          dv.setUint16(2, data.code[0], false)
        }
        break

      case 0x05:
        // 宏定义
        if (data.macro && data.macro.index !== undefined) {
          dv.setUint8(2, data.macro.index)
        }
        break

      case 0x06:
        // 文档未定义
        break

      case 0x07:
        // 禁用按键
        break

      case 0x08:
        // DPI 按键
        if (data.dpi && data.dpi.type !== undefined) {
          dv.setUint8(2, data.dpi.type)
        }
        break

      case 0x09:
        // 功能按键
        if (data.snipingFire) {
          dv.setUint8(2, data.snipingFire.type)
          dv.setUint8(1, data.snipingFire.fireType ?? 0)
        }
        break

      case 0x0a:
        // 自定义切换键
        if (data.diy && data.diy.type !== undefined) {
          dv.setUint8(2, data.diy.type)
        }
        break

      case 0x0b:
      // 前滚轮
      case 0x0c:
      // 侧滚轮
      case 0xff:
        // 默认按键
        dv.setUint32(0, 0xffffffff, true)
        if (data.wheel) {
          dv.setUint8(0, data.type)
          let valueType: number = data.wheel.valueType
          if (data.type === 0x0b || data.type === 0x0c) {
            // 写入相对该键默认方向的反向标志：0x00 顺向 / 0x01 反向
            const defaultDirection = WHEEL_DEFAULT_DIRECTION[index] ?? 0
            valueType = data.wheel.direction === defaultDirection ? 0x00 : 0x01
          }
          dv.setUint8(2, valueType)
          dv.setUint8(3, data.wheel.type)
        }
        break

      case 0x0d:
        // 任意键盘组合
        if (data.code && data.code.length) {
          dv.setUint8(1, data.code[0])
          dv.setUint8(2, data.code[1] ?? 0)
          dv.setUint8(3, data.code[2] ?? 0)
        }
        break

      default:
        break
    }
    return new Uint8Array(buffer)
  }

  /** 获取宏列表 */
  async getMacroList(): Promise<Macro[]> {
    return this.macroStore.getMacroList()
  }

  /** 流式获取宏列表 */
  async *getMacroListStream(batchSize: number = 3): AsyncIterable<Macro[]> {
    for await (const macros of this.macroStore.getMacroListStream(batchSize)) {
      yield macros
    }
  }

  /** 获取指定组的宏 */
  async getMacroByIndex(index: number): Promise<Macro> {
    return this.macroStore.getMacroByIndex(index)
  }

  /** 设置指定组宏数据 */
  async setMacroByIndex(macro: Macro, index: number): Promise<void> {
    return this.macroStore.setMacroByIndex(macro, index)
  }

  /** 获取指定组的压枪宏 */
  async getPressureMacroByIndex(index: number): Promise<PressureMacro> {
    return this.pressureMacroStore.getMacroByIndex(index)
  }

  /** 设置指定组的压枪宏 */
  async setPressureMacroByIndex(macro: PressureMacro, index: number): Promise<void> {
    return this.pressureMacroStore.setMacroByIndex(macro, index)
  }
}
